import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppAction, UserProfile as User, AppState, Role } from '@/types';
import { Button } from '@/components/ui/Button';
import {
  User as UserIcon, Clock, Mail, Camera, Save, Trash2, AlertTriangle,
  Smile, Coffee, Moon, Sun, Briefcase, Globe, Check, MapPin,
  Loader2, Sparkles, BookOpen, Microscope, Landmark,
  Scale, Rocket, PawPrint, Wrench, Soup, Laugh,
  Shield, Users, Layers, Lock, Info, EyeOff
} from 'lucide-react';
import { updateProfile, deleteUser } from 'firebase/auth';
import { doc, updateDoc, setDoc, getDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/firebase';
import { getAllTimezones, getTimezoneOffset } from '@/utils/timezoneUtils';

interface UserSettingsProps {
  user: User;
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' }
];

const SPARK_CATEGORIES = [
  { id: 'vocabulary', label: 'Vocabulary', icon: BookOpen, color: 'text-blue-500' },
  { id: 'animal', label: 'Animal Science', icon: PawPrint, color: 'text-emerald-500' },
  { id: 'cleaning', label: 'Cleaning Tips', icon: Wrench, color: 'text-sky-500' },
  { id: 'cooking', label: 'Cooking Hacks', icon: Soup, color: 'text-orange-500' },
  { id: 'history', label: 'Global History', icon: Landmark, color: 'text-amber-600' },
  { id: 'jokes', label: 'Dad Jokes', icon: Laugh, color: 'text-pink-500' },
  { id: 'logic', label: 'Logic & Fallacies', icon: Scale, color: 'text-indigo-500' },
  { id: 'science', label: 'General Science', icon: Microscope, color: 'text-lime-600' },
  { id: 'space', label: 'Astronomy', icon: Rocket, color: 'text-purple-500' },
  { id: 'culture', label: 'World Culture', icon: Globe, color: 'text-rose-500' },
];

export const UserSettings: React.FC<UserSettingsProps> = ({ user, state, dispatch }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const isInitialMount = useRef(true);

  // --- Hybrid Scope State ---
  const [preferredScope, setPreferredScope] = useState<string>(user.defaultScope || 'BOTH');

  // --- Form States ---
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState(user.name || '');
  const [avatarUrl, setAvatarUrl] = useState(user.avatar || '');
  const [statusMessage, setStatusMessage] = useState((user as any).statusMessage || 'online');
  const [selectedLang, setSelectedLang] = useState(user.preferredLanguage || i18n.language || 'en');

  // --- Timezone State ---
  const [selectedTz, setSelectedTz] = useState(user.timezone || 'UTC');
  const [autoDetectTz, setAutoDetectTz] = useState(user.autoDetectTz !== false);

  // --- Mailing List State ---
  const [devUpdateOptIn, setDevUpdateOptIn] = useState(false);
  const [isMailingListLoading, setIsMailingListLoading] = useState(true);

  // --- Knowledge Engine State ---
  const [activeSparks, setActiveSparks] = useState<string[]>(user.activeSparkCategories || []);

  const STATUS_OPTIONS = [
    { key: 'online', label: t('status.online', 'Online'), icon: Smile, color: 'text-emerald-500' },
    { key: 'at_work', label: t('status.at_work', 'At Work'), icon: Briefcase, color: 'text-blue-500' },
    { key: 'on_vacation', label: t('status.on_vacation', 'On Vacation'), icon: Sun, color: 'text-amber-500' },
    { key: 'night_shift', label: t('status.night_shift', 'Night Shift'), icon: Moon, color: 'text-indigo-500' },
    { key: 'busy', label: t('status.busy', 'Busy'), icon: Coffee, color: 'text-red-500' },
  ];

  useEffect(() => {
    if (user) {
      setDisplayName(user.name);
      setAvatarUrl(user.avatar || '');
      const names = user.name.split(' ');
      setFirstName(names[0] || '');
      setLastName(names.slice(1).join(' ') || '');
      setStatusMessage((user as any).statusMessage || 'online');
      setSelectedLang(user.preferredLanguage || i18n.language || 'en');
      setSelectedTz(user.timezone || 'UTC');
      setAutoDetectTz(user.autoDetectTz !== false);
      setActiveSparks(user.activeSparkCategories && user.activeSparkCategories.length > 0
        ? user.activeSparkCategories
        : ['vocabulary'] // Default to vocabulary if none set, for existing users
      );
      setPreferredScope(user.defaultScope || 'BOTH');
      fetchMailingListStatus();
    }
  }, [user.id, user.name, user.avatar, user.preferredLanguage, user.timezone, user.autoDetectTz, user.activeSparkCategories, user.defaultScope, i18n.language]);

  const fetchMailingListStatus = async () => {
    if (!user.email) return;
    try {
      const mailRef = doc(db, "mailing_list", user.email.toLowerCase());
      const snap = await getDoc(mailRef);
      setDevUpdateOptIn(snap.exists());
    } catch (e) {
      console.error("Error fetching mailing list status:", e);
    } finally {
      setIsMailingListLoading(false);
    }
  };

  const handleMailingListToggle = async () => {
    if (!user.email) return;
    const newValue = !devUpdateOptIn;
    setDevUpdateOptIn(newValue);

    try {
      const mailRef = doc(db, "mailing_list", user.email.toLowerCase());
      if (newValue) {
        await setDoc(mailRef, {
          email: user.email.toLowerCase(),
          userId: user.id,
          optedIn: true,
          subscribedAt: serverTimestamp(),
          listType: 'DEVELOPMENT_UPDATES'
        });
      } else {
        await deleteDoc(mailRef);
      }
    } catch (e) {
      console.error("Mailing list update failed:", e);
      setDevUpdateOptIn(!newValue);
    }
  };

  const toggleSparkCategory = (id: string) => {
    setActiveSparks(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (autoDetectTz) {
      try {
        const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (detectedTz && detectedTz !== selectedTz) {
          setSelectedTz(detectedTz);
        }
      } catch (e) {
        console.error("Timezone detection failed:", e);
      }
    }
  }, [autoDetectTz, selectedTz]);

  useEffect(() => {
    const persistTimezonePreference = async () => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }
      if (!auth.currentUser || !user.id) return;
      try {
        const userRef = doc(db, "users", user.id);
        await updateDoc(userRef, {
          timezone: selectedTz,
          autoDetectTz: autoDetectTz,
          updatedAt: new Date().toISOString()
        });
      } catch (e) {
        console.error("Silent timezone save failed:", e);
      }
    };
    persistTimezonePreference();
  }, [selectedTz, autoDetectTz, user.id]);

  const handleLanguageChange = async (langCode: string) => {
    setSelectedLang(langCode);
    i18n.changeLanguage(langCode);
    if (auth.currentUser) {
      try {
        const userRef = doc(db, "users", user.id);
        await updateDoc(userRef, { preferredLanguage: langCode });
      } catch (e) {
        console.error("Language sync failed:", e);
      }
    }
  };

  const handleSave = async () => {
    if (!auth.currentUser) return;
    setIsLoading(true);
    try {
      const fullDisplayName = `${firstName} ${lastName}`.trim() || displayName;

      await updateProfile(auth.currentUser, { // Re-added Firebase Auth profile update
        displayName: fullDisplayName,
        photoURL: avatarUrl
      });

      const userRef = doc(db, "users", user.id);

      const updates = {
        name: fullDisplayName,
        avatar: avatarUrl,
        statusMessage: statusMessage,
        preferredLanguage: selectedLang,
        timezone: selectedTz,
        autoDetectTz: autoDetectTz,
        activeSparkCategories: activeSparks,
        defaultScope: preferredScope,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(userRef, updates);

      dispatch({
        type: 'UPDATE_USER',
        payload: updates as Partial<User>
      });

      // If they changed their default scope, sync the global state
      dispatch({ type: 'SET_DATA_SCOPE', payload: preferredScope as any });

      alert(t('settings.profile_updated', 'Identity Synchronized.'));
    } catch (error) {
      console.error("Error updating profile:", error);
      alert(t('settings.update_failed', 'Failed to update registry.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmation = prompt(t('settings.delete_confirm_prompt', 'Type DELETE to permanently remove your account:'));
    if (confirmation !== 'DELETE' || !auth.currentUser) return;
    setIsLoading(true);
    try {
      // 🛰️ Cleaning up user-level data before authentication purge
      const userRef = doc(db, "users", user.id);
      await deleteDoc(userRef);
      await deleteUser(auth.currentUser);
      dispatch({ type: 'SET_USER', payload: null });
      navigate('/');
    } catch (error: any) {
      console.error("Account deletion failed:", error);
      // More robust error handling for specific Firebase errors
      if (error.code === 'auth/requires-recent-login') {
        alert(t('settings.delete_reauth_required', 'Please log in again to delete your account.'));
      } else {
        alert(t('settings.delete_failed_generic', 'Failed to delete account. Please try again later.'));
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8 pb-20 animate-in fade-in duration-500">
      {/* --- HEADER --- */}
      <div className="flex justify-between items-end border-b border-slate-100 dark:border-slate-800 pb-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic">
            {t('settings.title', 'User Profile')}
          </h1>
          <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] mt-1">
            {t('settings.subtitle', 'Identity & Regional Synchronization')}
          </p>
        </div>
        <Button onClick={handleSave} className="rounded-2xl px-8 shadow-lg shadow-indigo-500/20 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-widest h-12" disabled={isLoading}>
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {t('settings.save_button', 'Save Changes')}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="space-y-6">
          {/* Profile Card */}
          <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm text-center">
            <div className="relative inline-block mb-6">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800 border-4 border-white dark:border-slate-700 shadow-xl mx-auto">
                {avatarUrl ? <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" /> : (
                  <div className="w-full h-full flex items-center justify-center text-4xl font-black text-slate-300 uppercase">
                    {firstName.charAt(0) || user.name.charAt(0)}
                  </div>
                )}
              </div>
              <div className="absolute bottom-0 right-0 bg-indigo-600 text-white p-2 rounded-full shadow-lg border-2 border-white dark:border-slate-900 cursor-pointer transition-transform hover:scale-110">
                <Camera className="w-4 h-4" />
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-xs font-black uppercase text-slate-400 tracking-widest">{t('settings.current_status', 'Temporal Status')}</p>
              <div className="flex flex-wrap justify-center gap-2">
                {STATUS_OPTIONS.map(status => (
                  <button
                    key={status.key}
                    onClick={() => setStatusMessage(status.key)}
                    className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                      statusMessage === status.key
                        ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900'
                        : 'bg-white dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <status.icon className={`w-3 h-3 ${statusMessage === status.key ? '' : status.color}`} />
                      {status.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          {/* Knowledge Engine Toggles */}
          <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest flex items-center gap-2 italic">
              <Sparkles className="w-3 h-3 text-indigo-500" /> Dashboard Sparks
            </h3>
            <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto pr-2 no-scrollbar">
              {SPARK_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => toggleSparkCategory(cat.id)}
                  className={`flex items-center justify-between p-3 rounded-2xl transition-all border-2 ${
                    activeSparks.includes(cat.id)
                      ? 'bg-indigo-50 dark:bg-indigo-900/10 border-indigo-200 dark:border-indigo-800'
                      : 'bg-slate-50 dark:bg-slate-800/50 border-transparent text-slate-400'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <cat.icon className={`w-4 h-4 ${activeSparks.includes(cat.id) ? cat.color : 'text-slate-300'}`} />
                    <span className={`text-[11px] font-black uppercase tracking-wider ${activeSparks.includes(cat.id) ? 'text-slate-900 dark:text-white' : ''}`}>
                      {cat.label}
                    </span>
                  </div>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${activeSparks.includes(cat.id) ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700'}`}>
                    {activeSparks.includes(cat.id) && <Check className="w-3 h-3" />}
                  </div>
                </button>
              ))}
            </div>
          </div>
          {/* 🛡️ PRIVACY & HYBRID SCOPE SETTINGS */}
          <div className="bg-slate-900 text-white p-8 rounded-[2.5rem] border border-slate-800 shadow-xl space-y-6">
            <h3 className="text-xs font-black uppercase tracking-widest flex items-center gap-2 italic">
              <Shield className="w-3 h-3 text-emerald-400" /> Data Scoping
            </h3>

            <div className="space-y-4">
              <p className="text-[10px] font-bold text-slate-400 leading-relaxed">
                Choose how EverydayOS handles your data by default. "Hybrid" allows merged visibility of personal and shared registries.
              </p>

              <div className="space-y-2">
                {[
                  { id: 'PERSONAL', label: 'Personal Only', icon: Lock, desc: '/users/{uid}' },
                  { id: 'BOTH', label: 'Hybrid (Both)', icon: Layers, desc: 'Merged Context' },
                  { id: 'SHARED', label: 'Shared Only', icon: Users, desc: '/households/{hid}' }
                ].map(s => (
                  <button
                    key={s.id}
                    onClick={() => setPreferredScope(s.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-2xl transition-all border-2 ${
                      preferredScope === s.id
                        ? 'bg-white/10 border-indigo-500'
                        : 'bg-white/5 border-transparent opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <s.icon className={`w-4 h-4 ${preferredScope === s.id ? 'text-indigo-400' : 'text-slate-500'}`} />
                      <div className="text-left">
                        <p className="text-[10px] font-black uppercase">{s.label}</p>
                        <p className="text-[8px] font-bold text-slate-500">{s.desc}</p>
                      </div>
                    </div>
                    {preferredScope === s.id && <Check className="w-3 h-3 text-indigo-400" />}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-white/10">
              <div className="flex items-start gap-2 p-3 bg-emerald-500/10 rounded-xl">
                <Info className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-[9px] font-bold text-emerald-400/80 leading-tight">
                  Verification: Personal data is siloed. Household members cannot access your Private Partition.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {/* Identity Data Card (firstName, lastName, Email) */}
          <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
            <h3 className="text-lg font-black mb-6 flex items-center gap-2 text-slate-900 dark:text-white uppercase italic">
              <UserIcon className="w-5 h-5 text-indigo-500" /> {t('settings.personal_details', 'Identity Data')}
            </h3>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2 ml-2">{t('settings.first_name', 'First Name')}</label>
                  <input value={firstName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)} className="w-full p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border-none font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Alex" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2 ml-2">{t('settings.last_name', 'Last Name')}</label>
                  <input value={lastName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)} className="w-full p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border-none font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Doe" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2 ml-2">{t('settings.email', 'Comm Channel')}</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                  <input value={user.email} disabled className="w-full pl-12 p-4 bg-slate-100 dark:bg-slate-950 rounded-2xl border-none font-bold text-slate-500 cursor-not-allowed" />
                </div>
              </div>
            </div>
          </div>
          {/* Regional Sync (Language/Timezone) */}
          <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest flex items-center gap-2 italic">
                <Globe className="w-3 h-3 text-indigo-500" /> Regional Sync
              </h3>
            </div>
            <div className="flex gap-2 flex-wrap">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                    selectedLang.startsWith(lang.code)
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-500/20'
                      : 'bg-white dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700 hover:border-indigo-400'
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.label}</span>
                </button>
              ))}
            </div>
            <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest flex items-center gap-2 italic">
                  <Clock className="w-3 h-3 text-indigo-500" /> Timezone
                </h3>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <span className="text-[9px] font-black uppercase text-slate-400 group-hover:text-indigo-500 transition-colors">
                    {autoDetectTz ? 'Automatic' : 'Manual'}
                  </span>
                  <div className="relative">
                    <input type="checkbox" className="sr-only peer" checked={autoDetectTz} onChange={(e) => setAutoDetectTz(e.target.checked)} />
                    <div className="w-8 h-4 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:bg-indigo-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4"></div>
                  </div>
                </label>
              </div>
              <div className="relative">
                <select
                  disabled={autoDetectTz}
                  value={selectedTz}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedTz(e.target.value)}
                  className={`w-full p-3 rounded-2xl bg-slate-50 dark:bg-slate-800 border-none font-bold text-xs text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all ${autoDetectTz ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {getAllTimezones().map(tz => (
                    <option key={tz} value={tz}>{tz.replace(/_/g, ' ')} ({getTimezoneOffset(tz)})</option>
                  ))}
                </select>
                {autoDetectTz && <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-indigo-500 animate-pulse" />}
              </div>
            </div>
          </div>
          {/* Purge Registry (Logic Unchanged) */}
          <div className="bg-red-50 dark:bg-red-900/10 p-8 rounded-[2.5rem] border border-red-100 dark:border-red-900/30">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div className="flex gap-4">
                <div className="p-3 bg-red-100 dark:bg-red-900/40 text-red-600 rounded-2xl h-fit"><AlertTriangle className="w-6 h-6" /></div>
                <div>
                  <h3 className="text-lg font-black text-red-900 dark:text-red-400 uppercase italic leading-none">{t('settings.delete_title', 'Purge Registry')}</h3>
                  <p className="text-[10px] font-black uppercase text-red-700 dark:text-red-300/80 mt-2 max-w-md tracking-tighter">Deletion is irreversible. All household data associated with this node will be shredded.</p>
                </div>
              </div>
              <Button onClick={handleDeleteAccount} className="w-full md:w-auto h-12 rounded-xl font-black uppercase tracking-widest bg-red-600 hover:bg-red-700 text-white transition-all shadow-lg shadow-red-500/20 text-xs px-6">
                <Trash2 className="w-4 h-4 mr-2" /> {t('settings.delete_button', 'Delete Account')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};